import _ from 'lodash'
import * as React from 'react'
import { Form, Spinner, Table } from 'react-bootstrap'
import { Grid } from '@mui/material'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import colors from '../../constants/colors'
import config from '../../constants/config'
import { JobStatusT } from '../../types/Job'
import { AxiosInstance } from '../../utils/RequestMethods'
import { parseFloatTotal } from '../../utils/utilsT'

// ── API Types ─────────────────────────────────────────────────────────────────
interface LineItem {
    item: string
    name: string
    total: number
}

interface JobFinancial {
    id: number
    date: string
    dateData: string   // "May 2026" | "Q2 2026" | "2026"
    jobStatus: string
    revenue: number
    cogs: number
    expenses: number
    profit: number
    revenueItems: LineItem[]
    cogsItems: LineItem[]
    expenseItems: LineItem[]
}

// ── Chart / aggregation bucket ────────────────────────────────────────────────
interface PeriodBucket {
    dateData: string
    jobs: number
    revenue: number
    cogs: number
    expenses: number
    profit: number
}

// text before first "," in item label
const itemLabel = (item: string): string => (item || '').split(',')[0].trim()

// Group raw jobs into period buckets for the chart — sorted by earliest job date
const groupJobsByPeriod = (jobs: JobFinancial[]): PeriodBucket[] => {
    const map: Record<string, PeriodBucket> = {}
    const minDate: Record<string, string> = {}   // earliest ISO date per bucket

    for (const job of jobs) {
        const key = job.dateData
        if (!map[key]) {
            map[key] = { dateData: key, jobs: 0, revenue: 0, cogs: 0, expenses: 0, profit: 0 }
            minDate[key] = job.date
        } else if (job.date < minDate[key]) {
            minDate[key] = job.date
        }
        map[key].jobs += 1
        map[key].revenue += job.revenue || 0
        map[key].cogs += job.cogs || 0
        map[key].expenses += job.expenses || 0
        map[key].profit += job.profit || 0
    }

    return Object.values(map).sort((a, b) =>
        minDate[a.dateData] < minDate[b.dateData] ? -1 : 1
    )
}

// Group raw jobs by actual day (YYYY-MM-DD) for the chart
const groupJobsByDay = (jobs: JobFinancial[]): PeriodBucket[] => {
    const map: Record<string, PeriodBucket> = {}

    for (const job of jobs) {
        const key = job.date   // "2026-05-10"
        if (!map[key]) {
            map[key] = { dateData: key, jobs: 0, revenue: 0, cogs: 0, expenses: 0, profit: 0 }
        }
        map[key].jobs += 1
        map[key].revenue += job.revenue || 0
        map[key].cogs += job.cogs || 0
        map[key].expenses += job.expenses || 0
        map[key].profit += job.profit || 0
    }

    return Object.values(map).sort((a, b) =>
        a.dateData < b.dateData ? -1 : 1
    )
}

// Generate every day of a month as empty PeriodBuckets (fills gaps between job days)
const buildAllDaysInMonth = (year: number, monthIndex: number): PeriodBucket[] => {
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
    const result: PeriodBucket[] = []
    for (let d = 1; d <= daysInMonth; d++) {
        const day = String(d).padStart(2, '0')
        const month = String(monthIndex + 1).padStart(2, '0')
        result.push({ dateData: `${year}-${month}-${day}`, jobs: 0, revenue: 0, cogs: 0, expenses: 0, profit: 0 })
    }
    return result
}

// Aggregate line items across all jobs, grouped by item key — display name takes priority
const aggregateLineItems = (jobs: JobFinancial[], key: 'revenueItems' | 'cogsItems' | 'expenseItems') => {
    const allItems = jobs.flatMap(j => j[key] || [])
    const grouped = _.groupBy(allItems, li => itemLabel(li.item))
    return Object.entries(grouped).map(([label, items]) => ({
        label: items[0]?.name || label,
        total: _.sumBy(items, 'total'),
    })).sort((a, b) => b.total - a.total)
}

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
]

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4']

const currentYear = new Date().getFullYear()

const formatCurrency = (value: number): string => {
    const amount = Number(parseFloatTotal(value) || 0)
    return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

// ── Summary Card ──────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, color, icon }: {
    label: string; value: number; color: string; icon: string
}) => (
    <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '20px 24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        borderLeft: `4px solid ${color}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    }}>
        <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>{icon} {label}</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: '#222' }}>
            {formatCurrency(value)}
        </span>
    </div>
)

interface FinancialsProps { }

const Financials: React.FunctionComponent<FinancialsProps> = ({ }): JSX.Element => {
    const [jobs, setJobs] = React.useState<JobFinancial[]>([])
    const [totalJobs, setTotalJobs] = React.useState(0)
    const [loading, setLoading] = React.useState(false)
    const [filterBy, setFilterBy] = React.useState('Month')
    const [filterMonth, setFilterMonth] = React.useState(MONTHS[new Date().getMonth()])
    const [filterQuarter, setFilterQuarter] = React.useState('Q1')
    const [filterYear, setFilterYear] = React.useState(String(currentYear))
    const [filterStatus, setFilterStatus] = React.useState('all')
    const [availableYears, setAvailableYears] = React.useState<string[]>([String(currentYear)])
    const isFirstRender = React.useRef(true)

    React.useEffect(() => { fetchOverview() }, [])

    // Auto-apply when any filter changes (skip initial mount)
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        fetchData()
    }, [filterBy, filterMonth, filterQuarter, filterYear, filterStatus])

    const buildParams = (overrides: Record<string, any> = {}): Record<string, any> => {
        const params: Record<string, any> = {
            jobStatus: filterStatus === 'all' ? [] : [filterStatus],
            filterBy,
            all: true,
            ...overrides,
        }
        if (filterYear) {
            params.year = filterYear
            params.all = false
            if (filterBy === 'Month') params.month = MONTHS.indexOf(filterMonth) + 1
            if (filterBy === 'Quarter') params.quarter = parseInt(filterQuarter.replace('Q', ''), 10)
        }
        return params
    }

    // Initial load: /financials/overview → { totalJobs, years, jobs[] } filtered to current month
    const fetchOverview = async () => {
        setLoading(true)
        try {
            const res = await AxiosInstance.get(config.url.financialsOverview, {
                data: {
                    jobStatus: [],
                    filterBy: 'Month',
                    year: currentYear,
                    month: new Date().getMonth() + 1,
                    all: true,
                }
            })
            if (res?.data) {
                const { years, jobs: rawJobs, totalJobs: tj } = res.data as {
                    years: number[]; jobs: JobFinancial[]; totalJobs: number
                }
                if (Array.isArray(years) && years.length > 0) {
                    setAvailableYears(years.map(String).sort((a, b) => Number(b) - Number(a)))
                }
                if (Array.isArray(rawJobs)) setJobs(rawJobs)
                if (tj !== undefined) setTotalJobs(tj)
            }
        } catch (err) {
            console.log('fetchOverview err', err)
        }
        setLoading(false)
    }

    // Apply filter: /financials → { totalJobs, jobs[] }
    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await AxiosInstance.get(config.url.financials, { data: buildParams() })
            if (res?.data) {
                const { jobs: rawJobs, totalJobs: tj } = res.data as {
                    jobs: JobFinancial[]; totalJobs: number
                }
                if (Array.isArray(rawJobs)) setJobs(rawJobs)
                if (tj !== undefined) setTotalJobs(tj)
            }
        } catch (err) {
            console.log('fetchData err', err)
        }
        setLoading(false)
    }

    // ── Totals (from raw jobs) ────────────────────────────────────────────────
    const totalRevenue = _.sumBy(jobs, 'revenue') || 0
    const totalCogs = _.sumBy(jobs, 'cogs') || 0
    const totalExpenses = _.sumBy(jobs, 'expenses') || 0
    const netProfit = totalRevenue - totalCogs - totalExpenses

    // ── Aggregate line items across all jobs ──────────────────────────────────
    const aggregatedRevenue = React.useMemo(() => aggregateLineItems(jobs, 'revenueItems'), [jobs])
    const aggregatedCogs = React.useMemo(() => aggregateLineItems(jobs, 'cogsItems'), [jobs])
    const aggregatedExpenses = React.useMemo(() => aggregateLineItems(jobs, 'expenseItems'), [jobs])

    // ── Chart data: every day of the month when filtering by Month, else grouped by day ──
    const chartData = React.useMemo(() => {
        const byDay = groupJobsByDay(jobs)
        if (filterBy === 'Month' && filterYear) {
            const monthIndex = MONTHS.indexOf(filterMonth)
            const allDays = buildAllDaysInMonth(Number(filterYear), monthIndex)
            const byDayMap: Record<string, PeriodBucket> = {}
            for (const b of byDay) byDayMap[b.dateData] = b
            return allDays.map(d => byDayMap[d.dateData] ?? d)
        }
        return byDay
    }, [jobs, filterBy, filterMonth, filterYear])

    // ── Filter label for headings ─────────────────────────────────────────────
    const filterLabel = !filterYear ? 'All Time' :
        filterBy === 'Month' ? `${filterMonth} ${filterYear}` :
            filterBy === 'Quarter' ? `${filterQuarter} ${filterYear}` :
                filterYear

    // ── X-Axis label ─────────────────────────────────────────────────────────
    const xAxisLabel = !filterYear ? 'Year' :
        filterBy === 'Month' ? 'Month' :
            filterBy === 'Quarter' ? 'Quarter' : 'Year'

    return (
        <div style={{ margin: '28px 32px', fontFamily: 'inherit' }}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontWeight: 700, fontSize: 24 }}>💰 Financials</h2>
                {loading && <Spinner animation="border" size="sm" variant="primary" />}
            </div>

            {/* ── Summary Cards ────────────────────────────────────────────── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
                marginBottom: 28,
            }}>
                <SummaryCard label="Total Revenue" value={totalRevenue} color="#4f46e5" icon="📈" />
                <SummaryCard label="Total COGS" value={totalCogs} color="#f59e0b" icon="📦" />
                <SummaryCard label="Total Expenses" value={totalExpenses} color="#ef4444" icon="💸" />
                <SummaryCard label="Net Profit" value={netProfit} color={netProfit >= 0 ? '#10b981' : '#ef4444'} icon="🏆" />
                {/* Jobs Card */}
                <div style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: '20px 24px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                    borderLeft: '4px solid #6366f1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                }}>
                    <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>🗂 Total Jobs</span>
                    <span style={{ fontSize: 26, fontWeight: 700, color: '#222' }}>{totalJobs.toLocaleString()}</span>
                </div>
            </div>

            {/* ── Chart ───────────────────────────────────────────────────── */}
            <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: '20px 24px 20px 8px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                marginBottom: 28,
            }}>
                <h5 style={{ marginBottom: 16, fontWeight: 600, paddingLeft: 16 }}>
                    Financial Overview — {filterLabel}
                </h5>
                <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="dateData"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(val: string) =>
                                filterBy === 'Month' ? String(parseInt(val.split('-')[2], 10)) : val
                            }
                            label={{
                                value: xAxisLabel,
                                position: 'insideBottom',
                                offset: -16,
                                style: { fontSize: 12, fill: '#888' },
                            }}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            tickFormatter={v => formatCurrency(Number(v))}
                            label={{
                                value: 'Dollar Amount, $',
                                angle: -90,
                                position: 'insideLeft',
                                offset: -8,
                                style: { fontSize: 12, fill: '#888' },
                            }}
                        />
                        <Tooltip
                            formatter={(val: number, name: string) => [
                                formatCurrency(val),
                                name,
                            ]}
                        />
                        <Legend verticalAlign="top" height={36} />
                        <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#4f46e5" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="cogs" name="COGS" stroke="#f59e0b" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="profit" name="Profit" stroke="#10b981" strokeWidth={2.5} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* ── Filters ─────────────────────────────────────────────────── */}
            <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: '20px 24px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                marginBottom: 28,
            }}>
                <p style={{ margin: '0 0 14px 0', fontWeight: 600, fontSize: 14, color: '#444' }}>
                    🗓 Filter
                </p>
                <Grid container spacing={3}>
                    {(() => {
                        // 3 fields when filterBy=Year, 4 fields when Month/Quarter
                        const colSize: any = filterBy === 'Year' ? 4 : 3

                        return (<>

                            {/* Filter By */}
                            <Grid item xs={12} sm={colSize}>
                                <Form.Label>Filter By</Form.Label>
                                <Form.Control
                                    as='select'
                                    className='input-material'
                                    value={filterBy}
                                    onChange={(e: any) => {
                                        const val = e.target.value
                                        setFilterBy(val)
                                        if (val !== 'Year' && !filterYear) {
                                            setFilterYear(availableYears[0] || String(currentYear))
                                        }
                                    }}>
                                    {['Year', 'Month', 'Quarter'].map(v => <option key={v} value={v}>{v}</option>)}
                                </Form.Control>
                            </Grid>

                            {/* Month */}
                            {filterBy === 'Month' && (
                                <Grid item xs={12} sm={colSize}>
                                    <Form.Label>Month</Form.Label>
                                    <Form.Control
                                        as='select'
                                        className='input-material'
                                        value={filterMonth}
                                        onChange={(e: any) => setFilterMonth(e.target.value)}>
                                        {MONTHS.map(v => <option key={v} value={v}>{v}</option>)}
                                    </Form.Control>
                                </Grid>
                            )}

                            {/* Quarter */}
                            {filterBy === 'Quarter' && (
                                <Grid item xs={12} sm={colSize}>
                                    <Form.Label>Quarter</Form.Label>
                                    <Form.Control
                                        as='select'
                                        className='input-material'
                                        value={filterQuarter}
                                        onChange={(e: any) => setFilterQuarter(e.target.value)}>
                                        {QUARTERS.map(v => <option key={v} value={v}>{v}</option>)}
                                    </Form.Control>
                                </Grid>
                            )}

                            {/* Year */}
                            <Grid item xs={12} sm={colSize}>
                                <Form.Label>Year</Form.Label>
                                <Form.Control
                                    as='select'
                                    className='input-material'
                                    value={filterYear || (filterBy === 'Year' ? 'All' : availableYears[0])}
                                    onChange={(e: any) => setFilterYear(e.target.value === 'All' ? '' : e.target.value)}>
                                    {filterBy === 'Year' && <option value='All'>All</option>}
                                    {availableYears.map(v => <option key={v} value={v}>{v}</option>)}
                                </Form.Control>
                            </Grid>

                            {/* Job Status */}
                            <Grid item xs={12} sm={colSize}>
                                <Form.Label>Job Status</Form.Label>
                                <Form.Control
                                    as='select'
                                    className='input-material'
                                    value={filterStatus}
                                    onChange={(e: any) => setFilterStatus(e.target.value)}>
                                    <option value='all'>All</option>
                                    <option value={JobStatusT.scheduled}>Scheduled</option>
                                    <option value={JobStatusT.completed}>Completed</option>
                                </Form.Control>
                            </Grid>

                        </>)
                    })()}
                </Grid>
            </div>

            {/* ── Income Statement Table ───────────────────────────────────── */}
            <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: '20px 24px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            }}>
                <h5 style={{ marginBottom: 16, fontWeight: 600 }}>📋 Income Statement — {filterLabel}</h5>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : jobs.length === 0 ? (
                    <p style={{ color: '#aaa', textAlign: 'center', padding: 32 }}>No data available.</p>
                ) : (
                    <Table responsive style={{ fontSize: 14, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ width: '65%', padding: '10px 14px' }}>Description</th>
                                <th style={{ textAlign: 'right', padding: '10px 14px' }}>Amount</th>
                                <th style={{ textAlign: 'right', padding: '10px 14px', color: '#888', whiteSpace: 'nowrap' }}>% of Revenue</th>
                            </tr>
                        </thead>
                        <tbody>

                            {/* ── Revenue ── */}
                            <tr style={{ background: '#f8faff' }}>
                                <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 700, color: '#4f46e5', fontSize: 13, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                    📈 Revenue
                                </td>
                            </tr>
                            {aggregatedRevenue.length > 0
                                ? aggregatedRevenue.map((li, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '7px 14px 7px 36px', color: '#555' }}>{li.label || '—'}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#333' }}>{formatCurrency(li.total)}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#999', fontSize: 12 }}>
                                            {totalRevenue ? `${((li.total / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                        </td>
                                    </tr>
                                ))
                                : <tr><td colSpan={3} style={{ padding: '7px 14px 7px 36px', color: '#bbb', fontStyle: 'italic' }}>No line items</td></tr>
                            }
                            <tr style={{ background: '#eef2ff', borderTop: '1px solid #c7d2fe' }}>
                                <td style={{ padding: '9px 14px', fontWeight: 700, color: '#4f46e5' }}>Total Revenue</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', fontWeight: 700, color: '#4f46e5' }}>{formatCurrency(totalRevenue)}</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', color: '#999', fontSize: 12 }}>{totalJobs.toLocaleString()} jobs</td>
                            </tr>

                            {/* ── COGS ── */}
                            <tr style={{ background: '#fffbf0' }}>
                                <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 700, color: '#b45309', fontSize: 13, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                    📦 Cost of Goods Sold (COGS)
                                </td>
                            </tr>
                            {aggregatedCogs.length > 0
                                ? aggregatedCogs.map((li, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '7px 14px 7px 36px', color: '#555' }}>{li.label || '—'}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#333' }}>{formatCurrency(li.total)}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#999', fontSize: 12 }}>
                                            {totalRevenue ? `${((li.total / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                        </td>
                                    </tr>
                                ))
                                : <tr><td colSpan={3} style={{ padding: '7px 14px 7px 36px', color: '#bbb', fontStyle: 'italic' }}>No line items</td></tr>
                            }
                            <tr style={{ background: '#fef3c7', borderTop: '1px solid #fcd34d' }}>
                                <td style={{ padding: '9px 14px', fontWeight: 700, color: '#b45309' }}>Total COGS</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', fontWeight: 700, color: '#b45309' }}>{formatCurrency(totalCogs)}</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', color: '#999', fontSize: 12 }}>
                                    {totalRevenue ? `${((totalCogs / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                </td>
                            </tr>

                            {/* ── Gross Profit ── */}
                            <tr style={{ background: '#f0fdf4', borderTop: '2px solid #86efac', borderBottom: '2px solid #86efac' }}>
                                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#15803d', fontSize: 15 }}>🏆 Gross Profit</td>
                                <td style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 700, color: '#15803d', fontSize: 15 }}>
                                    {formatCurrency(totalRevenue - totalCogs)}
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 14px', color: '#15803d', fontSize: 12 }}>
                                    {totalRevenue ? `${(((totalRevenue - totalCogs) / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                </td>
                            </tr>

                            {/* ── Expenses ── */}
                            <tr style={{ background: '#fff5f5' }}>
                                <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626', fontSize: 13, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                                    💸 Expenses
                                </td>
                            </tr>
                            {aggregatedExpenses.length > 0
                                ? aggregatedExpenses.map((li, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '7px 14px 7px 36px', color: '#555' }}>{li.label || '—'}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#333' }}>{formatCurrency(li.total)}</td>
                                        <td style={{ textAlign: 'right', padding: '7px 14px', color: '#999', fontSize: 12 }}>
                                            {totalRevenue ? `${((li.total / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                        </td>
                                    </tr>
                                ))
                                : <tr><td colSpan={3} style={{ padding: '7px 14px 7px 36px', color: '#bbb', fontStyle: 'italic' }}>No line items</td></tr>
                            }
                            <tr style={{ background: '#fee2e2', borderTop: '1px solid #fca5a5' }}>
                                <td style={{ padding: '9px 14px', fontWeight: 700, color: '#dc2626' }}>Total Expenses</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(totalExpenses)}</td>
                                <td style={{ textAlign: 'right', padding: '9px 14px', color: '#999', fontSize: 12 }}>
                                    {totalRevenue ? `${((totalExpenses / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                </td>
                            </tr>

                            {/* ── Net Profit ── */}
                            <tr style={{
                                background: netProfit >= 0 ? '#f0fdf4' : '#fff1f2',
                                borderTop: `3px solid ${netProfit >= 0 ? '#22c55e' : '#f43f5e'}`,
                            }}>
                                <td style={{ padding: '12px 14px', fontWeight: 800, fontSize: 16, color: netProfit >= 0 ? '#15803d' : '#be123c' }}>
                                    🏆 Net Profit
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px 14px', fontWeight: 800, fontSize: 16, color: netProfit >= 0 ? '#15803d' : '#be123c' }}>
                                    {formatCurrency(netProfit)}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px 14px', fontSize: 12, fontWeight: 600, color: netProfit >= 0 ? '#15803d' : '#be123c' }}>
                                    {totalRevenue ? `${((netProfit / totalRevenue) * 100).toFixed(1)}%` : '—'}
                                </td>
                            </tr>

                        </tbody>
                    </Table>
                )}
            </div>

            <br /><br />
        </div>
    )
}

export default Financials
